import { FinanceResponseError, requireFinancePage, requireFinanceAccount } from '../../lib/financeResponse';
import React, { useEffect, useRef, useState } from 'react';
import { adminAPI, SettlementTrader, TraderSettlementHistory, TraderSettlementRequest } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Pagination } from '../ui/pagination';

const money = (value: number) => typeof value === 'number' && Number.isFinite(value)
    ? value.toLocaleString('ru-RU', { maximumFractionDigits: 6 }) : '—';
const key = (id: string) => `admin-trader-settlement-pending:${id}`;
const units = (value: string): number | null => {
    const normalized = value.trim().replace(',', '.');
    if (!/^\d+(?:\.\d{1,6})?$/.test(normalized) || Number(normalized) >= 1e9) return null;
    const result = Math.round(Number(normalized) * 1e6);
    return Number.isSafeInteger(result) ? result : null;
};
const errorText = (error: any) => error instanceof FinanceResponseError ? error.message : typeof error?.response?.data?.detail === 'string'
    ? error.response.data.detail : 'Не удалось выполнить запрос. Повторите попытку.';
const date = (value: string) => new Date(/[Zz]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`)
    .toLocaleString('ru-RU', { timeZone: 'UTC' }) + ' UTC+0';

export const TraderSettlementPanel: React.FC<{ canManage: boolean; initialTraderId?: string }> = ({ canManage, initialTraderId }) => {
    const [traders, setTraders] = useState<SettlementTrader[]>([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(0);
    const [selected, setSelected] = useState<SettlementTrader | null>(null);
    const [history, setHistory] = useState<TraderSettlementHistory | null>(null);
    const [historyPage, setHistoryPage] = useState(1);
    const [amount, setAmount] = useState('');
    const [commission, setCommission] = useState('0');
    const [comment, setComment] = useState('');
    const [pending, setPending] = useState<TraderSettlementRequest | null>(null);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const loadFailed = !!loadError;
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [refresh, setRefresh] = useState(0);
    const submitting = useRef(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const [selectedId, setSelectedId] = useState<string | undefined>(initialTraderId);
    const amountUnits = units(amount), commissionUnits = units(commission);
    const totalUnits = (amountUnits ?? 0) + (commissionUnits ?? 0);
    const valid = amountUnits !== null && amountUnits > 0 && commissionUnits !== null
        && totalUnits <= Math.round((selected?.available_balance ?? 0) * 1e6);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setLoadError('');
        Promise.all([adminAPI.getSettlementTraders(page), selectedId
            ? adminAPI.getTraderSettlements(selectedId, historyPage) : Promise.resolve(null)])
            .then(([list, detail]) => {
                if (!active) return;
                requireFinancePage(list);
                list.items.forEach(requireFinanceAccount);
                if (selectedId) {
                    requireFinancePage(detail);
                    requireFinanceAccount(detail?.trader);
                }
                setLoadError('');
                setTraders(list.items); setPages(list.pages); setHistory(detail);
                if (detail) setSelected(detail.trader);
            }).catch(err => { if (active) { setLoadError(errorText(err)); } })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [page, selectedId, historyPage, refresh]);

    useEffect(() => {
        if (selectedId) panelRef.current?.scrollIntoView?.({ block: 'start' });
    }, [selectedId]);

    const select = (trader: SettlementTrader) => {
        if (submitting.current) return;
        setSelected(trader); setSelectedId(trader.id); setHistory(null); setHistoryPage(1); setError(''); setNotice('');
    };

    useEffect(() => {
        let saved: TraderSettlementRequest | null = null;
        try {
            const value = JSON.parse(selectedId ? localStorage.getItem(key(selectedId)) || 'null' : 'null');
            if (value && typeof value.request_id === 'string' && typeof value.amount === 'string'
                && typeof value.commission === 'string' && typeof value.comment === 'string') saved = value;
        } catch { /* Ignore invalid browser storage. */ }
        setPending(saved); setAmount(saved?.amount || ''); setCommission(saved?.commission || '0'); setComment(saved?.comment || '');
    }, [selectedId]);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selected || !canManage || submitting.current || (!pending && (!valid || loading || loadFailed))) return;
        const request = pending || { request_id: crypto.randomUUID(), amount: amount.trim().replace(',', '.'),
            commission: commission.trim().replace(',', '.'), comment: comment.trim() };
        try { localStorage.setItem(key(selected.id), JSON.stringify(request)); }
        catch { setError('Не удалось сохранить запрос в браузере. Проведение не начато.'); return; }
        setPending(request); submitting.current = true; setBusy(true); setError(''); setNotice('');
        try {
            const result = await adminAPI.createTraderSettlement(selected.id, request);
            requireFinanceAccount(result?.trader);
            if (typeof result?.settlement?.total !== 'number' || !Number.isFinite(result.settlement.total)) throw new FinanceResponseError();
            localStorage.removeItem(key(selected.id)); setPending(null);
            setSelected(result.trader); setAmount(''); setCommission('0'); setComment(''); setHistoryPage(1);
            setNotice(`Сеттл проведён. Списано ${money(result.settlement.total)} USDT.`);
            setRefresh(value => value + 1);
        } catch (err: any) {
            // A network error or server failure may follow a committed debit: keep the exact request.
            if ([400, 403, 404, 422].includes(err?.response?.status)
                || (err?.response?.status === 409 && typeof err?.response?.data?.detail === 'string' && err.response.data.detail.startsWith('Недостаточно свободных средств'))) {
                localStorage.removeItem(key(selected.id)); setPending(null);
            }
            setError(errorText(err));
            setRefresh(value => value + 1);
        } finally { submitting.current = false; setBusy(false); }
    };

    return <Card ref={panelRef} className="scroll-mt-4">
        <CardHeader><CardTitle>Трейдеры</CardTitle></CardHeader>
        <CardContent className="space-y-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">Сеттлы с трейдерами: сумма и комиссия списываются с рабочего баланса. Все суммы в USDT.</p>
        {(error || loadError) && <p role="alert" className="text-red-600 dark:text-red-300">{error || loadError}</p>}
        {notice && <p role="status" className="text-emerald-700 dark:text-emerald-300">{notice}</p>}
        <Button variant="outline" disabled={busy || loading} onClick={() => setRefresh(value => value + 1)}>Обновить</Button>
        {loading && <p role="status">Загрузка…</p>}
        {!selected ? <>
        <div className="overflow-x-auto"><table className="w-full text-sm text-left">
            <thead><tr>{['Трейдер', 'Рабочий, USDT', 'Страховой, USDT', 'Доступно, USDT'].map(title => <th key={title} className="p-3">{title}</th>)}</tr></thead>
            <tbody>{traders.map(trader => <tr key={trader.id} className="border-t hover:bg-slate-50 dark:hover:bg-slate-900">
                <td className="p-3"><button className="text-blue-600 dark:text-blue-300 underline disabled:text-slate-400 disabled:no-underline" disabled={busy || !trader.balance_ready} onClick={() => select(trader)}>{trader.username || trader.id}</button></td>
                <td className="p-3">{trader.balance_ready ? money(trader.balance) : '—'}</td>
                <td className="p-3">{trader.balance_ready ? money(trader.insurance_balance) : '—'}</td>
                <td className="p-3">{trader.balance_ready ? money(trader.available_balance) : '—'}</td>
            </tr>)}</tbody>
        </table></div>
        {!loading && !loadFailed && traders.length === 0 && <p>Трейдеров пока нет.</p>}
        <Pagination currentPage={page} totalPages={pages} onPageChange={setPage} disabled={busy || loading} />
        </> : <>
            <Button variant="outline" disabled={busy} onClick={() => {
                setSelected(null); setSelectedId(undefined); setHistory(null); setPending(null); setError(''); setNotice('');
            }}>Все трейдеры</Button>
            <h3 className="text-lg font-semibold">{selected.username}</h3>
            <p>Рабочий: {money(selected.balance)} USDT · Страховой: {money(selected.insurance_balance)} USDT · Доступно: {money(selected.available_balance)} USDT</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">Резервы: {money(selected.reserved_balance)} USDT · Заморожено: {money(selected.frozen_balance)} USDT</p>
            {canManage && <form onSubmit={submit} className="space-y-3 max-w-2xl">
                <Label htmlFor="trader-settlement-amount">Сумма, USDT</Label>
                <Input id="trader-settlement-amount" inputMode="decimal" value={amount} disabled={busy || !!pending} onChange={e => setAmount(e.target.value)} />
                <Label htmlFor="trader-settlement-commission">Комиссия, USDT</Label>
                <Input id="trader-settlement-commission" inputMode="decimal" value={commission} disabled={busy || !!pending} onChange={e => setCommission(e.target.value)} />
                <Label htmlFor="trader-settlement-comment">Комментарий</Label>
                <Input id="trader-settlement-comment" maxLength={1000} value={comment} disabled={busy || !!pending} onChange={e => setComment(e.target.value)} />
                {amountUnits !== null && commissionUnits !== null && <p>Итого к списанию: {money(totalUnits / 1e6)} USDT. Остаток рабочего баланса: {money((Math.round(selected.balance * 1e6) - totalUnits) / 1e6)} USDT.</p>}
                {!pending && amount !== '' && !valid && <p className="text-amber-700 dark:text-amber-300">Укажите положительную сумму и неотрицательную комиссию до 6 знаков после запятой, меньше 1 млрд USDT каждую. Итог не должен превышать доступный баланс.</p>}
                {pending && <p>Запрос сохранён. Повторная отправка проверит результат и не спишет средства повторно.</p>}
                <Button type="submit" disabled={busy || loading || (!pending && (loadFailed || !valid))}>{busy ? 'Проведение…' : pending ? 'Повторить запрос' : 'Провести сеттл'}</Button>
            </form>}
            <h4 className="font-semibold">История сеттлов</h4>
            <div className="overflow-x-auto"><table className="w-full text-sm text-left">
                <thead><tr>{['Дата', 'Сумма, USDT', 'Комиссия, USDT', 'Списано, USDT', 'Баланс до', 'Баланс после', 'Комментарий', 'Администратор'].map(title => <th key={title} className="p-2">{title}</th>)}</tr></thead>
                <tbody>{history?.items.map(row => <tr key={row._id} className="border-t">
                    <td className="p-2">{date(row.created_at)}</td>
                    {[row.amount, row.commission, row.total, row.balance_before, row.balance_after].map((value, i) => <td className="p-2" key={i}>{money(value)}</td>)}
                    <td className="p-2 whitespace-pre-wrap break-words">{row.comment}</td><td className="p-2">{row.admin_username}</td>
                </tr>)}</tbody>
            </table></div>
            {!loading && !loadFailed && history?.items.length === 0 && <p>Сеттлов пока нет.</p>}
            <Pagination currentPage={historyPage} totalPages={history?.pages || 0} onPageChange={setHistoryPage} disabled={busy || loading} />
        </>}
        </CardContent>
    </Card>;
};
